import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { PCGEntry } from 'app/api/pcg.api.service';
import { AppState } from 'app/reducers';
import { agGridLocale } from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';

import { selectPCG, selectPCGLoading } from './pcg.selectors';

@Component({
    selector: 'pcg',
    templateUrl: './pcg.component.html',
    styleUrls: ['./pcg.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class PCGComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;
    public columnDefs;
    public defaultColDef;
    public sideBar;
    public statusBar;
    public rowBuffer;
    public localeText;
    public modules;
    public keyUp = new Subject<KeyboardEvent>();
    public rowData$: Observable<PCGEntry[]>;
    public loading$: Observable<boolean>;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

    constructor(
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>
    ) {
        this.logger = this.amplifyService.logger('PCGComponent');
        this._unsubscribeAll = new Subject();
        this.modules = AllModules;

        this.localeText = agGridLocale.fr;
        this.columnDefs = [
            { headerName: 'Code', field: 'id' },
            { headerName: 'Libellé', field: 'label' },
        ];
        this.defaultColDef = {
            enableValue: false,
            enableRowGroup: false,
            enablePivot: false,
            sortable: true,
            filter: true,
            resizable: true
        };
        this.sideBar = null;
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectPCGLoading));

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            )
            .subscribe();

        this.rowData$ = this.store.pipe(select(selectPCG));
    }

    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
    }
}
